import { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { MapContext } from 'os-elements';
import { addMapMode, removeMapMode } from 'client/store/mapSlice/mapSlice';
import { clearDrawStatus } from 'client/store/drawSlice/drawSlice';
import { drawStates, mapModes } from 'client/utils/constants/appStates';
import defaultAriaProps from 'client/utils/constants/ariaPropTypes';
import Button from 'client/components/aria/Button/Button';
import ConfirmIcon from 'client/components/aria/Icons/ConfirmIcon/ConfirmIcon';
import DeleteIcon from 'client/components/aria/Icons/DeleteIcon/DeleteIcon';
import DragHandleIcon from 'client/components/aria/Icons/DragHandleIcon/DragHandleIcon';
import Tooltip from 'client/components/aria/Tooltip/Tooltip';
import useFloatingPanelPosition from './useFloatingPanelPosition';
import useDrawLineTracking from './useDrawLineTracking';
import styles from './FloatingDrawPanel.module.scss';

const { DEFAULT, DRAWING, MODIFYING } = mapModes;
const { INVALID, EXCEEDS_EXTENT, VALID, OUTSIDE_SERVICE_AREA } = drawStates;

const METRES_PER_MILE = 1609.344;

/**
 * Formats a distance in metres to a human-readable metric string.
 * Uses metres (rounded) for values under 1000m, kilometres (1 decimal place) otherwise.
 */
const formatMetricDistance = (metres, intl) => {
    if (metres >= 1000) {
        const km = (metres / 1000).toFixed(1);
        return `${km} ${intl.formatMessage({ id: 'floatingDrawPanel.kilometres' })}`;
    }
    const m = Math.round(metres);
    return `${m} ${intl.formatMessage({ id: 'floatingDrawPanel.metres' })}`;
};

/**
 * Formats a distance in metres to a miles string (1 decimal place).
 */
const formatMilesDistance = (metres, intl) => {
    const miles = (metres / METRES_PER_MILE).toFixed(1);
    return `${miles} ${intl.formatMessage({ id: 'floatingDrawPanel.miles' })}`;
};

/**
 * Floating editing panel that appears alongside the active point during line drawing.
 *
 * Features:
 * - Anchored to the active vertex coordinate on the map
 * - Draggable via the drag handle
 * - Clamped to the map viewport on pan/zoom
 * - Shows live distance counter
 * - Confirm, delete, and move buttons
 */
const FloatingDrawPanel = ({ containerRef, onConfirm, onDeletePoint, ...props }) => {
    const intl = useIntl();
    const dispatch = useDispatch();
    const { map } = useContext(MapContext);
    const drawStatus = useSelector(state => state.draw?.status || []);
    const panelRef = useRef(null);
    const dragHandleRef = useRef(null);
    const [showDragTooltip, setShowDragTooltip] = useState(false);
    const clickStartPosRef = useRef(null);

    const {
        isVisible,
        isModifying,
        distance,
        activePointCoord,
        activePointIndex,
        coordinates,
        deleteActivePoint,
        finishDrawing
    } = useDrawLineTracking();

    const { position, isDragging, handleDragStart } = useFloatingPanelPosition({
        anchorCoordinate: activePointCoord,
        panelRef,
        containerRef
    });

    useEffect(() => {
        if (isDragging) {
            setShowDragTooltip(false);
        }
    }, [isDragging]);

    const handleDragHandleMouseDown = (e) => {
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        clickStartPosRef.current = { x: clientX, y: clientY };
        handleDragStart(e);
    };

    const handleDragHandleClick = (e) => {
        if (!clickStartPosRef.current) {
            return;
        }
        const dx = Math.abs(e.clientX - clickStartPosRef.current.x);
        const dy = Math.abs(e.clientY - clickStartPosRef.current.y);
        clickStartPosRef.current = null;
        if (dx < 3 && dy < 3) {
            setShowDragTooltip(true);
        }
    };

    if (!isVisible || !activePointCoord) {
        return null;
    }

    const handleConfirm = () => {
        if (onConfirm) {
            onConfirm();
            return;
        }

        if (!isModifying) {
            // Active drawing: finish the OL Draw interaction.
            // This triggers DrawAndModify's handleDrawEnd which handles
            // the AOI transfer, mode changes, and asset reveal.
            finishDrawing();
            return;
        }

        // Modifying mode: copy draw features to AOI (same as FinishButton)
        const drawSource = map?.getLayerById('draw')?.getSource();
        const aoiSource = map?.getLayerById('aoi')?.getSource();
        if (!drawSource || !aoiSource) {
            return;
        }

        aoiSource.clear();
        drawSource.getFeatures().forEach(f => aoiSource.addFeature(f.clone()));
        drawSource.clear();
        dispatch(removeMapMode(DRAWING));
        dispatch(removeMapMode(MODIFYING));
        dispatch(addMapMode(DEFAULT));
        dispatch(clearDrawStatus());
    };

    const handleDelete = () => {
        if (onDeletePoint) {
            onDeletePoint(activePointIndex);
            return;
        }
        deleteActivePoint();
    };

    const isConfirmDisabled =
        (drawStatus.length > 0 && !drawStatus.includes(VALID)) ||
        drawStatus.includes(INVALID) ||
        drawStatus.includes(EXCEEDS_EXTENT) ||
        drawStatus.includes(OUTSIDE_SERVICE_AREA);

    const canDelete = coordinates.length > 1;

    const confirmTooltipMessage = drawStatus.includes(EXCEEDS_EXTENT)
        ? intl.formatMessage({ id: 'floatingDrawPanel.tooltip.reduceLength' })
        : drawStatus.includes(OUTSIDE_SERVICE_AREA)
            ? intl.formatMessage({ id: 'floatingDrawPanel.tooltip.outsideServiceArea' })
            : null;

    return (
        <div
            ref={panelRef}
            className={styles.panel}
            style={{
                left: `${position.left}px`,
                top: `${position.top}px`
            }}
            role="region"
            aria-label={intl.formatMessage({ id: 'floatingDrawPanel.ariaLabel' })}
            aria-live="polite"
            data-testid={props['data-testid'] || 'floatingDrawPanel'}
        >
            <div className={styles.distanceContainer} data-testid="floatingDrawPanel.distance">
                <span className={['govuk-!-font-size-14', styles.distance].join(' ')}>
                    {formatMetricDistance(distance, intl)}
                </span>
                <span className={['govuk-!-font-size-14', styles.distanceMiles].join(' ')} data-testid="floatingDrawPanel.distanceMiles">
                    {formatMilesDistance(distance, intl)}
                </span>
            </div>
            <div className={styles.tooltipWrapper}>
                <Tooltip
                    isOpen={confirmTooltipMessage !== null}
                    data-testid="floatingDrawPanel.confirmTooltip"
                >
                    {confirmTooltipMessage}
                </Tooltip>
                <Button
                    className={styles.panelButton}
                    variant="success"
                    onPress={handleConfirm}
                    isDisabled={isConfirmDisabled}
                    elementType="button"
                    aria-label={intl.formatMessage({ id: 'floatingDrawPanel.confirm' })}
                    data-testid="floatingDrawPanel.confirm"
                >
                    <ConfirmIcon />
                </Button>
            </div>
            <Button
                className={styles.panelButton}
                variant="error"
                onPress={handleDelete}
                isDisabled={!canDelete}
                elementType="button"
                aria-label={intl.formatMessage({ id: 'floatingDrawPanel.delete' })}
                data-testid="floatingDrawPanel.delete"
            >
                <DeleteIcon />
            </Button>
            <div className={styles.tooltipWrapper}>
                <Tooltip
                    isOpen={showDragTooltip}
                    onClose={() => setShowDragTooltip(false)}
                    triggerRef={dragHandleRef}
                    data-testid="floatingDrawPanel.dragTooltip"
                >
                    {intl.formatMessage({ id: 'floatingDrawPanel.tooltip.dragToMove' })}
                </Tooltip>
                {/* Replace with a fix to the aria Button component */}
                <button
                    ref={dragHandleRef}
                    className={['govuk-button', styles.dragHandle, isDragging ? styles.dragging : null].join(' ')}
                    onMouseDown={handleDragHandleMouseDown}
                    onTouchStart={handleDragHandleMouseDown}
                    onClick={handleDragHandleClick}
                    aria-label={intl.formatMessage({ id: 'floatingDrawPanel.move' })}
                    aria-roledescription={intl.formatMessage({ id: 'floatingDrawPanel.move' })}
                    data-testid="floatingDrawPanel.dragHandle"
                    type="button"
                >
                    <DragHandleIcon />
                </button>
            </div>
        </div>
    );
};

FloatingDrawPanel.propTypes = {
    ...defaultAriaProps,
    containerRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
    onConfirm: PropTypes.func,
    onDeletePoint: PropTypes.func
};

export default FloatingDrawPanel;
